import { AttachmentMediaTypeEnum } from 'src/app/shared/models/misc.model';

import { SiteTypeEnum } from './enums';

class AttachmentError extends Error {
  translationPath: string;
  translationData: string;
  constructor(
    message: string,
    { translationPath, translationData }: { translationPath: string; translationData: string }
  ) {
    super(message);
    this.translationPath = translationPath;
    this.translationData = translationData;
  }
}

export class EditorConfig {
  spellCheckerLanguageCode: string;

  hasMaxCharsNumber = false;
  maxCharsNumber: number;
  hasMaxUTF8BytesNumber = false;
  maxUTF8BytesNumber: number;

  useTwitterPublicEditor = false;

  imageMimeTypes = 'image/*';
  videoMimeTypes = 'video/*';
  imageButNotGifMimeTypes = 'image/bmp,image/x-icon,image/jpeg,image/png,image/svg+xml,image/tiff,image/webp';
  gifMimeType = 'image/gif';
  fileMimeTypes =
    'text/plain,application/pdf,application/vnd.ms-powerpoint,application/msword,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  maxImageNumber = 1;
  maxGifNumber = 1;
  offersVideoAttachment = false;
  offersPictureAttachment = true;
  offersRichLinkAttachment = false;
  offersFileAttachment = false;
  offersAgentShortcutAttachment = false;
  offersGiphySearch = true;
  canMixAttachmentTypes = true;

  offersPlaceholderButton = true;
  offersFirstNamePlaceholder = true;
  offersFullNamePlaceholderWithArobaseMention = false;

  isLinkShortenerEnabled = true;

  pollMediaUploadedOnSocialNetwork = false;

  isSpellCheckerEnabledForCompany = false;
  videoMaxSizeMb = 15;
  pictureMaxSizeMb = 10;
  gifMaxSizeMb = 100;
  fileMaxSizeMb = 25;

  constructor(
    siteType: SiteTypeEnum,
    options: {
      isPrivate?: boolean;
      isPublish?: boolean;
      isSpellCheckerEnabledForCompany?: boolean;
      isEngagement?: boolean;
    }
  ) {
    const { isPrivate, isPublish, isSpellCheckerEnabledForCompany = false, isEngagement } = options;
    if (isPrivate) {
      this.offersAgentShortcutAttachment = true;
    }
    if (isPublish) {
      this.offersPlaceholderButton = false;
    }

    switch (siteType) {
      case SiteTypeEnum.Facebook:
        if (isPublish) {
          this.offersVideoAttachment = true;
          this.maxImageNumber = 100; // I dit not find any documented limit
          this.canMixAttachmentTypes = false;
          this.gifMaxSizeMb = 10;
        }
        if (isPrivate) {
          this.hasMaxCharsNumber = true;
          this.maxCharsNumber = 2000;
          this.offersVideoAttachment = true;
          this.videoMaxSizeMb = 20; // https://developers.facebook.com/docs/messenger-platform/reference/send-api/#attachment
          this.gifMaxSizeMb = 20;
          this.pictureMaxSizeMb = 20;
          this.offersFileAttachment = true;
          this.fileMaxSizeMb = 20;
        } else {
          this.offersFullNamePlaceholderWithArobaseMention = true;
          this.gifMaxSizeMb = 10;
          this.pictureMaxSizeMb = 10;
        }
        break;

      case SiteTypeEnum.Instagram2:
        this.imageMimeTypes = 'image/jpeg,image/png';
        if (isPrivate) {
          this.hasMaxUTF8BytesNumber = true;
          this.maxUTF8BytesNumber = 1000;
          this.pictureMaxSizeMb = 8;
          this.gifMaxSizeMb = 8;
          this.offersVideoAttachment = true;
          this.videoMaxSizeMb = 25; // https://developers.facebook.com/docs/messenger-platform/instagram/features/attachment-upload
        } else {
          this.offersPictureAttachment = false;
          this.offersGiphySearch = false;
        }
        break;

      case SiteTypeEnum.WhatsApp:
        this.hasMaxCharsNumber = true;
        this.maxCharsNumber = 4096;
        this.offersFirstNamePlaceholder = false;
        this.offersGiphySearch = false;
        this.imageMimeTypes = 'image/jpeg,image/png';
        this.videoMimeTypes = 'video/mp4,video/3gpp';
        this.pictureMaxSizeMb = 5;
        this.offersVideoAttachment = true;
        this.videoMaxSizeMb = 16; // https://developers.facebook.com/docs/whatsapp/api/media#upload
        this.offersFileAttachment = true;
        break;

      case SiteTypeEnum.Twitter:
        this.pictureMaxSizeMb = 5;
        this.gifMaxSizeMb = 15;
        // Waiting BE SP-5101
        // this.offersVideoAttachment = true;
        this.videoMaxSizeMb = 512; // https://developer.twitter.com/en/docs/twitter-api/v1/media/upload-media/overview
        this.offersAgentShortcutAttachment = false;

        if (!isPrivate) {
          // Public or Publish mode.
          this.hasMaxCharsNumber = true;
          this.maxCharsNumber = 280;
          this.useTwitterPublicEditor = true;
          this.offersFullNamePlaceholderWithArobaseMention = true;
          this.maxImageNumber = 4;
          this.canMixAttachmentTypes = false;
        }
        if (isPublish) {
          this.offersVideoAttachment = true;
        }
        break;

      case SiteTypeEnum.AppleBusinessChat:
        this.offersRichLinkAttachment = true;
        this.offersPlaceholderButton = false;
        this.pictureMaxSizeMb = 100;
        this.offersVideoAttachment = true;
        this.videoMaxSizeMb = 100; // https://register.apple.com/resources/messages/msp-rest-api/
        this.offersFileAttachment = true;
        break;

      case SiteTypeEnum.GoogleBusinessProfile:
        this.offersGiphySearch = false;
        this.offersPictureAttachment = false;
        break;

      case SiteTypeEnum.GoogleBusinessMessages:
        this.offersGiphySearch = false;
        this.pictureMaxSizeMb = 5;
        break;

      case SiteTypeEnum.InMessage:
        this.gifMaxSizeMb = 25;
        this.fileMimeTypes =
          'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        this.pictureMaxSizeMb = 25;
        this.offersFileAttachment = true;
        this.fileMaxSizeMb = 25;
        break;

      case SiteTypeEnum.Line:
        this.offersGiphySearch = false;
        this.imageMimeTypes = 'image/jpeg,image/png';
        this.pictureMaxSizeMb = 10;
        this.offersVideoAttachment = true;
        this.videoMaxSizeMb = 200; // https://developers.line.biz/en/reference/messaging-api/#video-message
        break;

      case SiteTypeEnum.Kakaotalk:
        this.hasMaxCharsNumber = true;
        this.maxCharsNumber = 1000;
        this.pollMediaUploadedOnSocialNetwork = true;
        this.gifMaxSizeMb = 5;
        this.pictureMaxSizeMb = 0.5;
        this.imageMimeTypes = 'image/jpeg,image/png';
        // this.offersVideoAttachment = true; // Waiting BE SP-5100
        this.offersGiphySearch = false;
        // this.offersGiphySearch = true; // Waiting BE SP-5179
        this.videoMaxSizeMb = 10;
        this.offersFileAttachment = !isEngagement;
        this.fileMaxSizeMb = 10;
        break;

      case SiteTypeEnum.Trustpilot:
        this.offersGiphySearch = false;
        this.offersPictureAttachment = false;
        break;

      case SiteTypeEnum.Linkedin:
        this.pictureMaxSizeMb = 25; // Limited by the backend to 25MB.
        this.gifMaxSizeMb = 10;
        this.imageMimeTypes = 'image/jpeg,image/png';
        break;

      case SiteTypeEnum.Tiktok: {
        this.hasMaxCharsNumber = true;
        this.maxCharsNumber = 150;

        this.offersVideoAttachment = false;
        this.offersPictureAttachment = false;
        this.offersRichLinkAttachment = false;
        this.offersFileAttachment = false;
        this.offersAgentShortcutAttachment = false;
        this.offersGiphySearch = false;
        this.canMixAttachmentTypes = false;
      }
    }

    this.isSpellCheckerEnabledForCompany = isSpellCheckerEnabledForCompany;
  }

  /**
   * Especially from the chat bot editor, allows to retrieve the current attachment limitation for the current channel.
   * @param attachmentMediaTypeEnum the media type constraints to get.
   * @returns the attachment limitations.
   */
  getAttachmentConfigurationFromScenario(attachmentMediaTypeEnum: AttachmentMediaTypeEnum): {
    supportedType: string;
    weight: number;
  } {
    switch (attachmentMediaTypeEnum) {
      case AttachmentMediaTypeEnum.Image:
        return {
          supportedType: this.imageMimeTypes,
          weight: this.pictureMaxSizeMb,
        };
      case AttachmentMediaTypeEnum.Document:
        return {
          supportedType: this.fileMimeTypes,
          weight: this.fileMaxSizeMb,
        };
      case AttachmentMediaTypeEnum.Video:
        return {
          supportedType: this.videoMimeTypes,
          weight: this.videoMaxSizeMb,
        };

      default:
        break;
    }
  }

  checkLimitations(attachmentMediaTypeEnum: AttachmentMediaTypeEnum, file: File) {
    const { size, type } = file;

    let translationPath;
    let translationData;
    const { supportedType, weight } = this.getAttachmentConfigurationFromScenario(attachmentMediaTypeEnum);

    const checkWeight = weight => {
      if (weight) {
        const maxWeight = (weight || 0) * 1024 * 1024;
        if (maxWeight && size >= maxWeight) {
          translationPath = 'media/upload/too-big/limitation';
          translationData = { max_size: weight };
          throw new AttachmentError('oops', { translationPath, translationData });
        }
      }
    };

    const checkSupportedTypes = supportedTypes => {
      const supportedContentTypes = supportedTypes?.split(',');

      if (supportedContentTypes?.length && !supportedContentTypes.includes(type)) {
        translationPath = 'media/upload/wrong-format/limitation';
        translationData = { supported_types: supportedTypes };
        throw new AttachmentError('oops', { translationPath, translationData });
      }
    };

    checkSupportedTypes(supportedType);
    checkWeight(weight);
  }
}
